import { stableRecordKey } from '../lib/rowIdentity';
import React from 'react';
import { motion } from 'framer-motion';
import { Users, Calendar, Clock, MapPin, Download, CreditCard, CheckCircle2, Loader2, ShieldCheck, Mail, Phone } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PatolaBorder } from '../components/luxury/PatolaBorder';
import { DocumentUpload } from '../components/DocumentUpload';
import { AddressLookup } from '../components/AddressLookup';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
export const fadeInUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.6,
    ease: [0.22, 1, 0.36, 1]
  }
};
export const SaturdayClubTermCalendar = ({
  fmtDay,
  info
}) => <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl font-bold text-center text-[hsl(var(--lux-royal))] mb-12" style={{
        fontFamily: 'Cormorant Garamond'
      }}>
              Term Dates
            </h2>
            <div className="grid gap-6" data-testid="saturday-club-term-calendar">
              {(info?.terms || []).length === 0 && <Card className="lux-ornate-card" data-testid="saturday-club-terms-empty">
                  <CardContent className="p-8 text-center">
                    <Calendar className="w-10 h-10 mx-auto mb-3 text-[hsl(var(--lux-gold))]" />
                    <p className="text-[hsl(var(--lux-ink)/0.8)] font-medium">Term dates for the coming year will be published here soon.</p>
                    <p className="text-sm text-[hsl(var(--lux-ink)/0.6)] mt-1">Please <a href="/contact" className="text-[hsl(var(--lux-royal))] font-semibold underline underline-offset-2">contact us</a> for the latest information.</p>
                  </CardContent>
                </Card>}
              {(info?.terms || []).map(term => <motion.div key={term.term_id} {...fadeInUp}>
                  <Card className={`lux-ornate-card ${term.status === 'current' ? 'border-2 border-[hsl(var(--lux-gold))]' : ''} ${term.status === 'past' ? 'opacity-60' : ''}`} data-testid={`saturday-club-term-${term.term_id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-xl font-bold text-[hsl(var(--lux-royal))]" style={{
                      fontFamily: 'Cormorant Garamond'
                    }}>{term.name}</h3>
                            {term.status === 'current' && <Badge className="bg-[hsl(var(--lux-gold-soft))] text-[hsl(var(--lux-royal))] border-[hsl(var(--lux-gold)/0.4)] text-[10px]">Current term</Badge>}
                            {term.status === 'past' && <Badge variant="outline" className="text-[10px]">Finished</Badge>}
                          </div>
                          <p className="text-sm text-[hsl(var(--lux-ink)/0.7)]">
                            <span className="font-semibold">Term Dates:</span> {term.start && term.end ? `${fmtDay(term.start)} - ${fmtDay(term.end)}` : 'To be confirmed'}
                          </p>
                          {term.notes && <p className="text-sm text-[hsl(var(--lux-ink)/0.6)] mt-1">{term.notes}</p>}
                        </div>
                        {term.half_term && <div className="text-sm text-[hsl(var(--lux-ink)/0.6)]">
                            <span className="font-semibold">Half Term:</span> {term.half_term}
                          </div>}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>;
export const ClubRegistrationLookup = ({
  chooseRegistration,
  findForm,
  findOpen,
  finding,
  found,
  handleFind,
  scrollToRegister,
  setFindForm,
  setFindOpen,
  setFound
}) => <Dialog open={findOpen} onOpenChange={o => {
  setFindOpen(o);
  if (!o) setFound([]);
}}>
        <DialogContent data-testid="saturday-club-find-dialog">
          <DialogHeader>
            <DialogTitle style={{
        fontFamily: 'Cormorant Garamond'
      }} className="text-2xl text-[hsl(var(--lux-royal))]">Pay Saturday Club fees</DialogTitle>
            <DialogDescription>Enter the parent email and the child&rsquo;s name you used when registering. We&rsquo;ll find the registration so you can pay for this term or the year.</DialogDescription>
          </DialogHeader>
          {found.length > 1 ? <div className="space-y-2" data-testid="saturday-club-find-results">
              <p className="text-sm text-[hsl(var(--lux-ink)/0.75)]">We found more than one child — which one is this payment for?</p>
              {found.map(r => <button key={r.registration_id} type="button" onClick={() => chooseRegistration(r)} className="w-full text-left rounded-lg border border-[hsl(var(--lux-gold)/0.4)] px-4 py-3 hover:bg-[hsl(var(--lux-royal-soft))] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--lux-gold))]" data-testid="saturday-club-find-result">
                  <span className="font-semibold text-[hsl(var(--lux-royal))]">{r.child_name}</span>
                  <span className="block text-xs text-[hsl(var(--lux-ink)/0.6)]">Ref {r.registration_id} · {r.payment_status === 'paid' ? `last paid £${Number(r.amount_paid || 0).toFixed(2)}${r.term_name ? ` (${r.term_name})` : ''}` : 'no payment yet'}</span>
                </button>)}
            </div> : <form onSubmit={handleFind} className="space-y-4">
              <div>
                <Label htmlFor="sc-find-email">Parent email</Label>
                <Input id="sc-find-email" type="email" autoComplete="email" value={findForm.email} onChange={e => setFindForm(f => ({
          ...f,
          email: e.target.value
        }))} placeholder="you@example.com" className="mt-1 h-11" data-testid="saturday-club-find-email" />
              </div>
              <div>
                <Label htmlFor="sc-find-child">Child&rsquo;s name</Label>
                <Input id="sc-find-child" value={findForm.child_name} onChange={e => setFindForm(f => ({
          ...f,
          child_name: e.target.value
        }))} placeholder="As entered on the registration" className="mt-1 h-11" data-testid="saturday-club-find-child" />
              </div>
              <Button type="submit" disabled={finding} className="w-full h-11 bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))]" data-testid="saturday-club-find-submit">
                {finding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />} Find my registration
              </Button>
              <p className="text-xs text-center text-[hsl(var(--lux-ink)/0.6)]">
                Not registered yet? <button type="button" className="underline text-[hsl(var(--lux-royal))]" onClick={() => {
          setFindOpen(false);
          scrollToRegister();
        }} data-testid="saturday-club-find-register-link">Register your child first</button> — payment follows immediately.
              </p>
            </form>}
        </DialogContent>
      </Dialog>;
export const SaturdayClubTermPricing = ({
  paymentsEnabled,
  registration,
  scrollToPayment,
  scrollToRegister,
  setFindOpen,
  setPayAgain,
  t
}) => <section className="py-16 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Card className="lux-ornate-card border-2 border-[hsl(var(--lux-gold))]">
              <PatolaBorder />
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-[hsl(var(--lux-royal))] mb-6 text-center" style={{
            fontFamily: 'Cormorant Garamond'
          }}>
                  {t('pricing_heading', 'Saturday Club Pricing')}
                </h2>
                <div className="grid sm:grid-cols-2 gap-6 mb-6">
                  <div className="text-center p-6 bg-[hsl(var(--lux-royal-soft))] rounded-lg" data-testid="saturday-club-term-pricing">
                    <div className="text-4xl font-bold text-[hsl(var(--lux-gold))] mb-2" style={{
                fontFamily: 'Cormorant Garamond'
              }}>£51</div>
                    <div className="text-sm text-[hsl(var(--lux-royal))] font-semibold">Per child, per term</div>
                  </div>
                  <div className="text-center p-6 bg-[hsl(var(--lux-royal-soft))] rounded-lg" data-testid="saturday-club-yearly-pricing">
                    <div className="text-4xl font-bold text-[hsl(var(--lux-gold))] mb-2" style={{
                fontFamily: 'Cormorant Garamond'
              }}>£150</div>
                    <div className="text-sm text-[hsl(var(--lux-royal))] font-semibold">Per child, per year</div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4" data-testid="saturday-club-pay-actions">
                  <Button type="button" onClick={() => registration ? (setPayAgain(true), scrollToPayment()) : setFindOpen(true)} disabled={!paymentsEnabled} className="bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))] h-11 px-6" data-testid="saturday-club-pay-fees-button">
                    <CreditCard className="w-4 h-4 mr-2" /> Pay fees online
                  </Button>
                  <Button type="button" variant="outline" onClick={scrollToRegister} className="h-11 px-6 border-[hsl(var(--lux-gold)/0.6)] text-[hsl(var(--lux-royal))] hover:bg-[hsl(var(--lux-royal-soft))]" data-testid="saturday-club-register-child-button">
                    <Users className="w-4 h-4 mr-2" /> Register a new child
                  </Button>
                </div>
                <p className="text-center text-sm text-[hsl(var(--lux-ink)/0.7)] mb-3">
                  Already registered? Use <strong>Pay fees online</strong> to pay this term&rsquo;s or the yearly fee &mdash; or choose a custom amount. New families register first (it takes a minute), then pay straight away.
                </p>
                {!paymentsEnabled && <p className="text-center text-sm text-[hsl(var(--lux-vermilion))] mb-3">Online payments are temporarily unavailable — please contact us to pay.</p>}
                <p className="text-center text-[hsl(var(--lux-ink)/0.7)]">
                  For more information, please{' '}
                  <a href="/contact" className="text-[hsl(var(--lux-royal))] font-semibold hover:text-[hsl(var(--lux-gold))] transition-colors">
                    get in touch with us
                  </a>
                  .
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>;
export const SaturdayClubSessionInfo = ({
  info,
  t
}) => <section className="py-16 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Card className="lux-ornate-card border-2 border-[hsl(var(--lux-gold))]">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-[hsl(var(--lux-royal))] mb-8 text-center" style={{
            fontFamily: 'Cormorant Garamond'
          }}>
                  {t('join_heading', 'Join Us Every Saturday')}
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[hsl(var(--lux-royal))]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[hsl(var(--lux-royal))] mb-1">When</h3>
                      <p className="text-[hsl(var(--lux-ink)/0.7)]" data-testid="saturday-club-session-info">{info?.session_info || 'Every Saturday | Check our Events page for specific times'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[hsl(var(--lux-royal))]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[hsl(var(--lux-royal))] mb-1">Where</h3>
                      <p className="text-[hsl(var(--lux-ink)/0.7)]" data-testid="saturday-club-venue">{info?.venue || '28 Handel Street, Nottingham NG3 1JE'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-[hsl(var(--lux-royal))]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[hsl(var(--lux-royal))] mb-1">Who Can Join</h3>
                      <p className="text-[hsl(var(--lux-ink)/0.7)]">All community members and their families are welcome{info && (info.age_min != null || info.age_max != null) ? ` · children aged ${info.age_min ?? 0}${info.age_max != null ? `-${info.age_max}` : '+'}` : ''}</p>
                    </div>
                  </div>
                  {(info?.contact_email || info?.contact_phone) && <div className="flex items-start gap-4" data-testid="saturday-club-contact">
                      <div className="w-12 h-12 rounded-full bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-[hsl(var(--lux-royal))]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[hsl(var(--lux-royal))] mb-1">Saturday Club Contact</h3>
                        {info.contact_email && <p className="text-[hsl(var(--lux-ink)/0.7)]"><a className="hover:text-[hsl(var(--lux-gold))]" href={`mailto:${info.contact_email}`}>{info.contact_email}</a></p>}
                        {info.contact_phone && <p className="text-[hsl(var(--lux-ink)/0.7)] flex items-center gap-1"><Phone className="w-3.5 h-3.5" /><a className="hover:text-[hsl(var(--lux-gold))]" href={`tel:${info.contact_phone}`}>{info.contact_phone}</a></p>}
                      </div>
                    </div>}
                </div>
                <div className="mt-8 text-center">
                  <a href="/contact" className="inline-flex items-center justify-center px-6 py-3 bg-[hsl(var(--lux-royal))] text-white rounded-lg hover:bg-[hsl(var(--lux-royal-2))] transition-colors font-semibold">
                    Get More Information
                  </a>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>;
export const SaturdayClubClosedNotice = ({
  form,
  handleDownloadForm,
  handleSubmit,
  info,
  loading,
  regFiles,
  setForm,
  setRegFiles,
  t
}) => <section id="saturday-club-register" className="py-16 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Card className="lux-ornate-card">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-[hsl(var(--lux-royal))] mb-6 text-center" style={{
            fontFamily: 'Cormorant Garamond'
          }}>
                  {t('register_heading', 'Register Here')}
                </h2>
                {info && !info.registrations_open ? <div className="p-6 rounded-lg bg-[hsl(var(--lux-parchment))] text-center text-[hsl(var(--lux-ink)/0.8)]" data-testid="saturday-club-closed-notice">
                    {info.closed_message || 'Registrations are currently closed.'}
                  </div> : <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label>Parent/Guardian Name *</Label>
                    <Input required value={form.name} onChange={e => setForm({
                ...form,
                name: e.target.value
              })} data-testid="saturday-club-parent-name-input" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Email *</Label>
                      <Input type="email" required value={form.email} onChange={e => setForm({
                  ...form,
                  email: e.target.value
                })} data-testid="saturday-club-email-input" />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <Input type="tel" required value={form.phone} onChange={e => setForm({
                  ...form,
                  phone: e.target.value
                })} data-testid="saturday-club-phone-input" />
                    </div>
                  </div>
                  <AddressLookup address={form.address} postcode={form.postcode} onChange={({
              address,
              postcode
            }) => setForm(f => ({
              ...f,
              address,
              postcode
            }))} testIdPrefix="saturday-club" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Child's Name *</Label>
                      <Input required value={form.child_name} onChange={e => setForm({
                  ...form,
                  child_name: e.target.value
                })} data-testid="saturday-club-child-name-input" />
                    </div>
                    <div>
                      <Label>Child's Age * {info && (info.age_min != null || info.age_max != null) && <span className="text-xs text-[hsl(var(--lux-ink)/0.55)]">(ages {info.age_min ?? 0}{info.age_max != null ? `-${info.age_max}` : '+'})</span>}</Label>
                      <Input type="number" required min={info?.age_min ?? 0} max={info?.age_max ?? 25} value={form.child_age} onChange={e => setForm({
                  ...form,
                  child_age: e.target.value
                })} data-testid="saturday-club-child-age-input" />
                    </div>
                  </div>

                  {/* Download Form Button */}
                  <div className="p-4 bg-[hsl(var(--lux-royal-soft))] rounded-lg">
                    <Button type="button" variant="outline" className="w-full border-[hsl(var(--lux-gold)/0.55)] text-[hsl(var(--lux-royal))] hover:bg-white" onClick={handleDownloadForm} data-testid="saturday-club-download-form-button">
                      <Download className="w-4 h-4 mr-2" />
                      Download Registration Form
                    </Button>
                    <p className="text-xs text-[hsl(var(--lux-ink)/0.6)] mt-2 text-center">
                      Download, complete, and upload the form below
                    </p>
                  </div>

                  {/* Supporting documents — global secure upload component (multiple files) */}
                  <div>
                    <Label>Upload Completed Form / Supporting Documents (Optional)</Label>
                    <p className="text-sm text-[hsl(var(--lux-ink)/0.7)] mb-2">
                      You can attach the completed registration form and any supporting documents (up to 5 files). On a phone you can photograph the form directly.
                    </p>
                    <DocumentUpload files={regFiles} onChange={setRegFiles} maxFiles={5} maxSizeMb={30} accept="documents+images+spreadsheets" label="" testId="saturday-club-file-upload" />
                  </div>

                  <Button type="submit" disabled={loading} className="w-full bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))] h-12 text-lg" data-testid="saturday-club-submit-button">
                    {loading ? 'Submitting...' : 'Submit Registration'}
                  </Button>
                </form>}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>;
export const ClubPaymentSection = ({
  allowCustom,
  customAmount,
  fmtDay,
  handlePay,
  payAgain,
  payAmount,
  payOption,
  payableTerms,
  paying,
  paymentsEnabled,
  pricing,
  registration,
  setCustomAmount,
  setPayAgain,
  setPayOption,
  setTermId,
  t,
  termId
}) => <section id="saturday-club-payment" className="py-16 bg-white" data-testid="saturday-club-payment-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="lux-ornate-card border-2 border-[hsl(var(--lux-gold))]">
              <PatolaBorder />
              <CardContent className="p-8">
                {registration.payment_status === 'paid' && !payAgain ? <div className="text-center" data-testid="saturday-club-paid-panel">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8 text-[hsl(var(--lux-royal))]" />
                    </div>
                    <h2 className="text-3xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{
            fontFamily: 'Cormorant Garamond'
          }}>Payment received</h2>
                    <p className="text-[hsl(var(--lux-ink)/0.75)]">
                      Thank you — <strong>£{Number(registration.amount_paid || 0).toFixed(2)}</strong> has been received for <strong>{registration.child_name}</strong>.
                      Reference <span className="font-mono">{registration.registration_id}</span>. A receipt has been emailed to you.
                    </p>
                    {registration.payments?.length > 1 && <ul className="mt-4 text-sm text-[hsl(var(--lux-ink)/0.7)] space-y-1" data-testid="saturday-club-payment-history">
                        {registration.payments.map((p, i) => <li key={stableRecordKey(p)}>£{Number(p.amount).toFixed(2)} &middot; {p.term_name || (p.payment_option === 'yearly' ? 'Yearly' : p.payment_option === 'custom' ? 'Custom amount' : 'Term')}{p.paid_at ? ` · ${new Date(p.paid_at).toLocaleDateString('en-GB')}` : ''}</li>)}
                      </ul>}
                    <Button type="button" variant="outline" onClick={() => setPayAgain(true)} disabled={!paymentsEnabled} className="mt-6 h-11 border-[hsl(var(--lux-gold)/0.6)] text-[hsl(var(--lux-royal))] hover:bg-[hsl(var(--lux-royal-soft))]" data-testid="saturday-club-pay-again-button">
                      <CreditCard className="w-4 h-4 mr-2" /> Pay for another term
                    </Button>
                  </div> : <>
                    <div className="text-center mb-6">
                      <Badge className="mb-3 bg-[hsl(var(--lux-gold-soft))] text-[hsl(var(--lux-royal))] border-[hsl(var(--lux-gold)/0.4)]">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {registration.payment_status === 'paid' ? 'Registered — paying for another term' : 'Registration received'}
                      </Badge>
                      <h2 className="text-3xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{
              fontFamily: 'Cormorant Garamond'
            }}>
                        Pay Saturday Club fees
                      </h2>
                      <p className="text-[hsl(var(--lux-ink)/0.75)]">
                        For <strong>{registration.child_name}</strong> &middot; reference <span className="font-mono text-sm" data-testid="saturday-club-registration-ref">{registration.registration_id}</span>
                      </p>
                    </div>

                    <RadioGroup value={payOption} onValueChange={setPayOption} className={`grid gap-4 ${allowCustom ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`} data-testid="saturday-club-payment-options">
                      {[{
              key: 'term',
              title: `£${Number(pricing.term).toFixed(Number.isInteger(Number(pricing.term)) ? 0 : 2)}`,
              sub: 'Per child, per term'
            }, {
              key: 'yearly',
              title: `£${Number(pricing.yearly).toFixed(Number.isInteger(Number(pricing.yearly)) ? 0 : 2)}`,
              sub: 'Per child, per year',
              badge: 'Best value'
            }, ...(allowCustom ? [{
              key: 'custom',
              title: 'Custom',
              sub: 'Choose your own amount'
            }] : [])].map(o => <label key={o.key} htmlFor={`sc-pay-${o.key}`} className={`relative cursor-pointer rounded-xl border-2 p-5 text-center transition-colors ${payOption === o.key ? 'border-[hsl(var(--lux-gold))] bg-[hsl(var(--lux-royal-soft))]' : 'border-[hsl(var(--lux-gold)/0.3)] bg-white hover:border-[hsl(var(--lux-gold)/0.6)]'}`} data-testid={`saturday-club-pay-option-${o.key}`}>
                          <RadioGroupItem value={o.key} id={`sc-pay-${o.key}`} className="absolute top-3 left-3" />
                          {o.badge && <span className="absolute -top-2.5 right-3 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[hsl(var(--lux-gold))] text-white">{o.badge}</span>}
                          <div className="text-3xl font-bold text-[hsl(var(--lux-gold))]" style={{
                fontFamily: 'Cormorant Garamond'
              }}>{o.title}</div>
                          <div className="text-sm text-[hsl(var(--lux-royal))] font-semibold mt-1">{o.sub}</div>
                        </label>)}
                    </RadioGroup>

                    {payOption === 'term' && payableTerms.length > 0 && <div className="mt-5 max-w-sm mx-auto" data-testid="saturday-club-term-picker">
                        <Label>Which term does this payment cover?</Label>
                        <Select value={termId} onValueChange={setTermId}>
                          <SelectTrigger className="mt-1 h-11 bg-white" data-testid="saturday-club-term-select"><SelectValue placeholder="Choose a term" /></SelectTrigger>
                          <SelectContent>
                            {payableTerms.map(t => <SelectItem key={t.term_id} value={t.term_id} data-testid="saturday-club-term-option">
                                {t.name}{t.start ? ` (${fmtDay(t.start)} - ${fmtDay(t.end)})` : ''}{t.status === 'current' ? ' · current' : ''}
                              </SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>}

                    {payOption === 'custom' && <div className="mt-5 max-w-xs mx-auto">
                        <Label htmlFor="sc-custom-amount">Amount (£)</Label>
                        <Input id="sc-custom-amount" type="number" min="0.5" step="0.01" inputMode="decimal" placeholder="e.g. 75" value={customAmount} onChange={e => setCustomAmount(e.target.value)} className="mt-1 h-11 text-lg" data-testid="saturday-club-custom-amount-input" />
                      </div>}

                    <Button type="button" onClick={handlePay} disabled={paying || !paymentsEnabled} className="mt-6 w-full bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))] h-12 text-lg" data-testid="saturday-club-pay-button">
                      {paying ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CreditCard className="w-5 h-5 mr-2" />}
                      {paying ? 'Redirecting to secure payment…' : `Pay £${payAmount.toFixed(2)} securely`}
                    </Button>
                    {!paymentsEnabled && <p className="text-center text-sm text-[hsl(var(--lux-vermilion))] mt-2">Online payments are temporarily unavailable — please contact us to pay.</p>}
                    <p className="text-center text-xs text-[hsl(var(--lux-ink)/0.6)] mt-3 flex items-center justify-center gap-1">
                      <ShieldCheck className="w-3.5 h-3.5" /> Card payments are processed securely by Stripe. Prefer to pay later? Your registration is saved and a payment link is in your confirmation email.
                    </p>
                  </>}
              </CardContent>
            </Card>
          </div>
        </section>;
